import { useLang } from 'hooks/lang';

import {
  FullProject,
  isInvoiceRepriceActive,
  isLeader,
  isOwner,
  projectStatusByCode,
  taskStatusesConfig,
} from 'entities';

import { CircleXIcon } from 'components/Icons';

import { useToast } from 'providers/Toast';

import { registerProjectOption } from '../options';

import { TrackerCancelationModal } from './TrackerModal';
import { MESSAGES } from '../../utils';
import { useProjectCancelation } from './cancelProject.hook';

const trackerTypes: Array<FullProject['type']> = ['TRACKER_ORIGIN', 'TRACKER_SEQUENCE'];

export default registerProjectOption({
  Icon: CircleXIcon,

  modal: 'CANCEL_PROJECT',
  overlay: TrackerCancelationModal,

  getLabel: (t) => t('cancel-project'),

  shortcut: {
    key: 'C',
    triggerKey: ['ctrl', 'shift'],
  },

  useOption: ({ loggedUser, project, openModal }) => {
    const { addToast } = useToast();
    const { t } = useLang();

    const { startCancelation } = useProjectCancelation();

    const hasOwnershipPower = [
      loggedUser.team.id === project.owner.team && isLeader(loggedUser),
      isOwner(project, loggedUser),
    ].some(Boolean);

    const canActuallyCancel = [
      projectStatusByCode[project.status].canCancel,
      !project.finishedOnce,
      !isInvoiceRepriceActive(project),
      hasOwnershipPower,
    ].every(Boolean);

    const handleCancelProjectClick = () => {
      if (!project.invoice.readyToInvoice) return addToast(MESSAGES.notSentToInvoice(t));

      if (project.finishedOnce) return addToast(MESSAGES.finishedBefore(t));

      if (project.tasks.some(({ status }) => status === taskStatusesConfig.pendingApproval.code))
        return addToast(MESSAGES.projectCancelWithPendingTasks(t));

      if (project.tasks.every(({ status }) => status === taskStatusesConfig.done.code))
        return addToast(MESSAGES.projectCancelWithDoneTasks(t));

      if (trackerTypes.includes(project.type)) return openModal('CANCEL_PROJECT');

      startCancelation();
    };

    return {
      action: handleCancelProjectClick,

      isEnabled: canActuallyCancel,
    };
  },
});
